/**
 * prefetch-pdfs.ts — Prefetch the technician's own PDFs for offline access (Fase 2b).
 *
 * After mirroring server reports, downloads PDF bytes for reports that have a server
 * pdfUrl but no local blob cached yet. This way the technician can view and download
 * their own PDFs offline without having to open each one individually first.
 *
 * Scope: TECHNICIAN OWN reports only. Do NOT use for admin (huge scope → lazy-only).
 *
 * ponytail: bounded at RECENT_MAX=10 most recently updated reports per run.
 *   - Upgrade path: lower the cap if storage quota is tight (Fase 3 can dynamically
 *     adjust via navigator.storage.estimate before deciding how many to prefetch).
 *   - Non-blocking: called with `void` in TechnicianPanel so it never delays the UI.
 *   - Best-effort: any individual fetch failure is silently caught; others continue.
 */

import { getPdf, savePdf } from "./pdf-cache.js";

/**
 * Maximum number of PDFs to prefetch per run (bounded to avoid large background
 * downloads on the first online session).
 *
 * ponytail: 10 is conservative. Upgrade to 20-50 once Fase 3 adds storage-aware
 * eviction, or let the user adjust via a setting.
 */
const RECENT_MAX = 10;

/**
 * Minimal report shape needed for prefetching.
 * Structurally compatible with `Informe` and `ServerReportMirrorInput`.
 */
export interface PrefetchInput {
  id: string;
  pdfUrl?: string | null;
  updatedAt: string;
}

/**
 * Prefetch PDFs for the technician's own synced reports.
 *
 * For each of the RECENT_MAX most recently updated reports:
 *   - Skip if no pdfUrl (never synced, no server PDF).
 *   - Skip if a blob is already cached in IDB (avoid redundant downloads).
 *   - Otherwise, fetch the PDF bytes and store in IDB for offline access.
 *
 * Best-effort: any fetch failure is silently ignored so one bad PDF does not
 * prevent the others from being cached.
 *
 * @param reports The technician's own server-synced reports (already filtered by
 *                the caller — this function does no role/author filtering).
 */
export async function prefetchOwnPdfs(reports: PrefetchInput[]): Promise<void> {
  // Select reports with a PDF URL, sorted newest first, capped at RECENT_MAX.
  const candidates = reports
    .filter((r) => !!r.pdfUrl)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, RECENT_MAX);

  for (const r of candidates) {
    // Best-effort: entire per-item block is wrapped so an IDB error on the cache
    // check (getPdf) does NOT abort the loop for the remaining reports (S2 fix).
    try {
      // Skip if already cached — no redundant network request.
      const existing = await getPdf(r.id);
      if (existing) continue;

      const res = await fetch(r.pdfUrl!);
      if (!res.ok) continue;
      const blob = await res.blob();
      await savePdf(r.id, blob);
    } catch {
      // Silently skip — do not block other prefetches.
    }
  }
}
